import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * `uploading` covers the wire transfer only. Once the last byte is sent the
 * server still has to hash the file, verify it and write the row, which for a
 * 200 MB IPA is several seconds of apparent silence — `verifying` is what keeps
 * the panel from sitting at a frozen 100% and looking hung.
 */
export type UploadStatus = "uploading" | "verifying" | "done" | "failed" | "cancelled";

export interface UploadJob {
  id: string;
  filename: string;
  appSlug: string;
  channel: string;
  version: string;
  sizeBytes: number;
  loadedBytes: number;
  status: UploadStatus;
  error: string | null;
  startedAt: number;
  /** Set when the transfer ends, so the panel can report how long it took. */
  finishedAt: number | null;
}

export interface UploadsState {
  jobs: UploadJob[];
}

const initialState: UploadsState = { jobs: [] };

const uploadsSlice = createSlice({
  name: "uploads",
  initialState,
  reducers: {
    uploadStarted(state, action: PayloadAction<UploadJob>) {
      // Newest first: the panel shows the job the operator just queued without
      // making them scroll past finished ones.
      state.jobs.unshift(action.payload);
    },
    uploadProgressed(state, action: PayloadAction<{ id: string; loadedBytes: number }>) {
      const job = state.jobs.find((entry) => entry.id === action.payload.id);
      if (!job || job.status !== "uploading") return;
      job.loadedBytes = action.payload.loadedBytes;
    },
    uploadVerifying(state, action: PayloadAction<string>) {
      const job = state.jobs.find((entry) => entry.id === action.payload);
      if (!job || job.status !== "uploading") return;
      job.status = "verifying";
      job.loadedBytes = job.sizeBytes;
    },
    uploadSucceeded(state, action: PayloadAction<string>) {
      const job = state.jobs.find((entry) => entry.id === action.payload);
      if (!job) return;
      job.status = "done";
      job.loadedBytes = job.sizeBytes;
      job.finishedAt = Date.now();
    },
    uploadFailed(state, action: PayloadAction<{ id: string; error: string }>) {
      const job = state.jobs.find((entry) => entry.id === action.payload.id);
      if (!job) return;
      job.status = "failed";
      job.error = action.payload.error;
      job.finishedAt = Date.now();
    },
    uploadCancelled(state, action: PayloadAction<string>) {
      const job = state.jobs.find((entry) => entry.id === action.payload);
      if (!job) return;
      job.status = "cancelled";
      job.finishedAt = Date.now();
    },
    uploadDismissed(state, action: PayloadAction<string>) {
      state.jobs = state.jobs.filter((job) => job.id !== action.payload);
    },
    finishedUploadsCleared(state) {
      state.jobs = state.jobs.filter(
        (job) => job.status === "uploading" || job.status === "verifying",
      );
    },
  },
});

export const {
  uploadStarted,
  uploadProgressed,
  uploadVerifying,
  uploadSucceeded,
  uploadFailed,
  uploadCancelled,
  uploadDismissed,
  finishedUploadsCleared,
} = uploadsSlice.actions;

export const uploadsReducer = uploadsSlice.reducer;

/**
 * Percent is reported from bytes rather than kept in state so it can never
 * disagree with the byte counters the panel prints beside it. It floors and
 * clamps: a browser that reports `loaded > total` (some do, counting the
 * multipart envelope) must not render "103%".
 */
export function uploadPercent(job: UploadJob): number {
  if (job.status === "done") return 100;
  if (job.sizeBytes === 0) return 0;
  return Math.min(100, Math.floor((job.loadedBytes / job.sizeBytes) * 100));
}
